import { useCallback, useEffect } from "react";
import useSWR from "swr";
import { vceFirestore, hvoFirestore } from "../../configs/FirebaseConfig";
import { FIRESTORE_ORDERS_TABLE } from "../../constants/FirestoreConstant";
import { ORDER_STATUS_CREATED } from "../../constants/OrderConstant";
import { DATE_FORMAT_DD_MM_YYYY } from "constants/DateConstant";
import * as dayjs from "dayjs";

/** Firestore's maximum number of writes in one batch. */
const BATCH_LIMIT = 500;

/**
 * Fetcher function for orders - fetches from both databases
 *
 * @param {string[]} datesToFetch - Delivery dates (DD-MM-YYYY). The page decides which: one date in
 *   the delivery-date view, the dates a work day covers in the work-day view (functions/workDay.js).
 *   At most 30, Firestore's limit for an `in` filter.
 */
const fetchOrdersFromFirestore = async (datesToFetch) => {
  console.log("[useOrders] 🔍 Fetching orders for delivery dates:", datesToFetch);
  console.log("[useOrders] 📋 Using collection:", FIRESTORE_ORDERS_TABLE);
  console.log("[useOrders] ⚙️ Filtering out status:", ORDER_STATUS_CREATED);

  // Check auth state
  const vceAuth = vceFirestore.app.auth();
  const hvoAuth = hvoFirestore.app.auth();
  console.log("[useOrders] 🟢 VCE auth user:", vceAuth.currentUser || "NOT LOGGED IN");
  console.log("[useOrders] 🟣 HVO auth user:", hvoAuth.currentUser || "NOT LOGGED IN");

  // "Friday also fetches Saturday" lived here until HVC-82. Saturday is packed on Friday, which the
  // work-day view now expresses for every day; the delivery-date view shows exactly one date.
  if (!datesToFetch || datesToFetch.length === 0) return [];

  // Fetch VCE orders
  console.log("[useOrders] 🟢 Fetching VCE orders for dates:", datesToFetch);
  let vceOrdersSnapshot;
  try {
    vceOrdersSnapshot = await vceFirestore
      .collection(FIRESTORE_ORDERS_TABLE)
      .where("delivery.date", "in", datesToFetch)
      .where("orderStatus", "!=", ORDER_STATUS_CREATED)
      .get();
    console.log("[useOrders] 🟢 VCE snapshot size:", vceOrdersSnapshot.size);
    console.log("[useOrders] 🟢 VCE empty:", vceOrdersSnapshot.empty);
  } catch (error) {
    console.error("[useOrders] ❌ VCE query failed:", error);
    console.error("[useOrders] ❌ Error code:", error.code);
    console.error("[useOrders] ❌ Error message:", error.message);
    // Return empty snapshot equivalent
    vceOrdersSnapshot = { docs: [], size: 0, empty: true };
  }

  const vceOrders = vceOrdersSnapshot.docs.map((doc) => {
    const data = doc.data();
    return {
      id: doc.id,
      ...data,
      shopSource: "VCE",
      shopName: "Vleescentrum",
    };
  });

  // Fetch HVO orders
  console.log("[useOrders] 🟣 Fetching HVO orders for dates:", datesToFetch);
  let hvoOrdersSnapshot;
  try {
    hvoOrdersSnapshot = await hvoFirestore
      .collection(FIRESTORE_ORDERS_TABLE)
      .where("delivery.date", "in", datesToFetch)
      .where("orderStatus", "!=", ORDER_STATUS_CREATED)
      .get();
    console.log("[useOrders] 🟣 HVO snapshot size:", hvoOrdersSnapshot.size);
    console.log("[useOrders] 🟣 HVO empty:", hvoOrdersSnapshot.empty);
  } catch (error) {
    console.error("[useOrders] ❌ HVO query failed:", error);
    console.error("[useOrders] ❌ Error code:", error.code);
    console.error("[useOrders] ❌ Error message:", error.message);
    // Return empty snapshot equivalent
    hvoOrdersSnapshot = { docs: [], size: 0, empty: true };
  }

  const hvoOrders = hvoOrdersSnapshot.docs
    .filter((doc) => !doc.data().isVleescentrum) // Filter out VCE orders
    .map((doc) => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        shopSource: "HVO",
        shopName: "Halalvlees Online",
      };
    });

  // Combine and sort by creation date (newest first)
  const combinedOrders = [...vceOrders, ...hvoOrders].sort((a, b) => b.createdAt - a.createdAt);
  console.log("[useOrders] ✅ Total orders returned:", combinedOrders.length);

  return combinedOrders;
};

/**
 * Hook to fetch and manage orders from both VCE and HVO Firestore databases
 * @param {Object} options - Configuration options
 * @param {boolean} options.realtime - Whether to use real-time listener (default: true)
 * @param {string} options.deliveryDate - Filter orders by delivery date (DD-MM-YYYY format, default: today)
 * @param {string[]} options.deliveryDates - Several delivery dates at once; wins over `deliveryDate`
 * @param {number} options.refreshInterval - Auto-refresh interval in ms (default: 30000 for realtime, 0 otherwise)
 * @returns {Object} - Orders data and utility functions
 */
export const useOrders = (options = {}) => {
  const {
    realtime = true,
    deliveryDate = dayjs().format(DATE_FORMAT_DD_MM_YYYY),
    deliveryDates,
    refreshInterval = realtime ? 30000 : 0, // 30 seconds for realtime, no refresh otherwise
  } = options;

  const datesToFetch = deliveryDates || [deliveryDate];
  // A string key: SWR compares keys by value, and a fresh array every render must not refetch
  const datesKey = datesToFetch.join(",");

  console.log("[useOrders] 📅 Hook initialized with delivery dates:", datesKey);
  console.log("[useOrders] 🔄 Realtime mode:", realtime);
  console.log("[useOrders] ⏱️  Refresh interval:", refreshInterval);

  // Use SWR for data fetching with caching
  const {
    data: orders,
    error,
    isLoading,
    mutate,
  } = useSWR([`orders`, datesKey], ([_, key]) => fetchOrdersFromFirestore(key ? key.split(",") : []), {
    refreshInterval,
    revalidateOnFocus: realtime,
    revalidateOnReconnect: realtime,
    dedupingInterval: 5000, // Dedupe requests within 5 seconds
  });

  /**
   * Manually refresh orders
   * @returns {Promise<Array>} - Updated orders
   */
  const refreshOrders = useCallback(() => {
    return mutate();
  }, [mutate]);

  /**
   * Find a specific order by ID in both databases
   * @param {string} orderId - The order ID to search for
   * @returns {Promise<Object|null>} - The order object or null if not found
   */
  const findOrderById = useCallback(async (orderId) => {
    if (!orderId) {
      console.warn("useOrders.findOrderById: No orderId provided");
      return null;
    }

    try {
      // Search in VCE first
      let snapshot = await vceFirestore
        .collection(FIRESTORE_ORDERS_TABLE)
        .where("orderId", "==", orderId)
        .limit(1)
        .get();

      if (!snapshot.empty) {
        const doc = snapshot.docs[0];
        return {
          id: doc.id,
          ...doc.data(),
          shopSource: "VCE",
          shopName: "Vleescentrum",
        };
      }

      // Search in HVO
      snapshot = await hvoFirestore.collection(FIRESTORE_ORDERS_TABLE).where("orderId", "==", orderId).limit(1).get();

      if (!snapshot.empty) {
        const doc = snapshot.docs[0];
        return {
          id: doc.id,
          ...doc.data(),
          shopSource: "HVO",
          shopName: "Halalvlees Online",
        };
      }

      return null;
    } catch (err) {
      console.error("Error finding order by ID:", err);
      return null;
    }
  }, []);

  /**
   * Update an order in the appropriate database with optimistic updates
   * @param {Object} order - The order object with shopSource property
   * @param {Object} updates - The fields to update
   * @returns {Promise<boolean>} - Success status
   */
  const updateOrder = useCallback(
    async (order, updates) => {
      if (!order || !order.id || !order.shopSource) {
        console.error("useOrders.updateOrder: Invalid order object");
        return false;
      }

      try {
        const firestore = order.shopSource === "VCE" ? vceFirestore : hvoFirestore;

        // Optimistically update the UI
        await mutate(
          async (currentOrders) => {
            // Update Firestore
            await firestore.collection(FIRESTORE_ORDERS_TABLE).doc(order.id).update(updates);

            // Return updated orders for cache
            return currentOrders?.map((o) =>
              o.id === order.id && o.shopSource === order.shopSource ? { ...o, ...updates } : o,
            );
          },
          {
            optimisticData: orders?.map((o) =>
              o.id === order.id && o.shopSource === order.shopSource ? { ...o, ...updates } : o,
            ),
            rollbackOnError: true,
            revalidate: false, // Don't revalidate immediately, update will show instantly
          },
        );

        return true;
      } catch (err) {
        console.error("Error updating order:", err);
        return false;
      }
    },
    [mutate, orders],
  );

  /**
   * Write top-level fields on many orders at once: one batch per shop (chunked at Firestore's 500),
   * then the cache, without a refetch. Built for the print status (`fulfilment`), where a print run
   * covers a whole work day and one `updateOrder` per order would be a hundred round trips.
   *
   * Only top-level keys: the cache merge is a shallow spread, and a dot-path key would land in the
   * cache as a literal "a.b" property.
   *
   * A batch is all or nothing, so a rules refusal fails the whole shop: every order of that shop is
   * reported in `failed`, none is silently dropped.
   *
   * @param {Array<{order: Object, updates: Object}>} entries
   * @returns {Promise<{written: Object[], failed: Array<{order: Object, error: string}>}>}
   */
  const updateOrdersFields = useCallback(
    async (entries) => {
      const written = [];
      const failed = [];
      const byShop = { VCE: [], HVO: [] };
      (entries || []).forEach((entry) => {
        if (byShop[entry?.order?.shopSource] && entry.order.id) byShop[entry.order.shopSource].push(entry);
        else failed.push({ order: entry?.order, error: "Onbekende bestelling" });
      });

      for (const [shopSource, shopEntries] of Object.entries(byShop)) {
        if (shopEntries.length === 0) continue;
        const firestore = shopSource === "VCE" ? vceFirestore : hvoFirestore;
        for (let start = 0; start < shopEntries.length; start += BATCH_LIMIT) {
          const chunk = shopEntries.slice(start, start + BATCH_LIMIT);
          try {
            const batch = firestore.batch();
            chunk.forEach(({ order, updates }) =>
              batch.update(firestore.collection(FIRESTORE_ORDERS_TABLE).doc(order.id), updates),
            );
            await batch.commit();
            written.push(...chunk);
          } catch (err) {
            console.error(`[useOrders] ❌ Batch update ${shopSource} failed:`, err);
            chunk.forEach(({ order }) => failed.push({ order, error: err?.message || String(err) }));
          }
        }
      }

      if (written.length > 0) {
        const updatesByKey = new Map(written.map(({ order, updates }) => [`${order.shopSource}|${order.id}`, updates]));
        await mutate(
          (currentOrders) =>
            currentOrders?.map((o) => {
              const updates = updatesByKey.get(`${o.shopSource}|${o.id}`);
              return updates ? { ...o, ...updates } : o;
            }),
          { revalidate: false },
        );
      }

      return { written: written.map(({ order }) => order), failed };
    },
    [mutate],
  );

  /**
   * Get orders filtered by status
   * @param {string} status - Order status to filter by
   * @returns {Array} - Filtered orders
   */
  const getOrdersByStatus = useCallback(
    (status) => {
      if (!status || status === "all") return orders;
      return orders.filter((order) => order.orderStatus === status);
    },
    [orders],
  );

  /**
   * Get orders filtered by shop source
   * @param {string} shopSource - "VCE" or "HVO"
   * @returns {Array} - Filtered orders
   */
  const getOrdersByShop = useCallback(
    (shopSource) => {
      if (!shopSource) return orders;
      return orders.filter((order) => order.shopSource === shopSource);
    },
    [orders],
  );

  return {
    orders: orders || [],
    isLoading,
    error,
    refreshOrders,
    findOrderById,
    updateOrder,
    updateOrdersFields,
    getOrdersByStatus,
    getOrdersByShop,
  };
};
